// V22 — Income Statement builder, period-scoped.
//
// Architecture (mirrors V21's CF rebuild):
//   Read from QBO ProfitAndLoss for the actual period, classify by
//   canonical mapping leaf, sum. No mapping is consulted for MATH; we
//   consult it only for PRESENTATION (which QBO accounts belong on
//   which IS line for the firm's canonical layout).
//
// Why this exists
//   V20's IS builder (`buildIncomeStatement` in lib/reports.js) called
//   `sectionTotal(accounts, mapping, sectionKey)` which read each
//   account's `a.balance` from the Trial Balance. The TB returns
//   YTD-from-FY-start values for IS accounts regardless of
//   `period_start`. As a result, the IS silently showed FYTD for any
//   non-FYTD reporting window (e.g., "Prior Month" April 2026 rendered
//   the FYTD title "Four Months Ended..." and FYTD values). V21 fixed
//   this for the CF. V22 fixes it for the IS.
//
// Inputs
//   accounts      same list V20 consumed (used here only for mapping
//                 leaf → account_id resolution)
//   mapping       canonical mapping defining which QBO account_ids
//                 roll up into each IS leaf (income / cogs / opex /
//                 other_income / other_expense)
//   plPeriod      QBO P&L for the CURRENT period: { section_totals,
//                 detail_lines: [{section, name, qbo_account_id, value}],
//                 net_income, ... }
//   plPeriodPrior QBO P&L for the prior-year-equivalent period (same
//                 calendar offset, one year earlier). null/missing →
//                 PY columns render as zeros + hasPrior=false.
//
// Output shape matches V20's buildIncomeStatement so the renderer
// swaps in without churn:
//   {
//     sections: { revenue, cogs, opex, otherIncome, otherExpense },
//     totals:   { totalRevenue, totalCOGS, grossProfit, totalOpex,
//                 incomeFromOps, totalOtherIncome, totalOtherExpense,
//                 netIncome, ebitda },
//     prior:    { ...same shape with PY values },
//     hasPrior: boolean,
//     builder:  'v22',
//   }

import { SECTION_SIGN } from './reports.js';

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// ───────────────────────────────────────────────────────────────────────────
// Index detail_lines by qbo_account_id for O(1) per-account lookup.
// Falls back to name-match for any line where QBO didn't return an id
// (rare, but happens for synthetic / system-generated lines).
// ───────────────────────────────────────────────────────────────────────────
function buildAccountValueMap(plPeriod, accounts) {
  const byId = new Map();  // qbo_account_id (string) → value
  const byName = new Map(); // account name (string, lower) → value
  const sectionById = new Map(); // qbo_account_id (string) → QBO native section group
  if (!plPeriod || !Array.isArray(plPeriod.detail_lines)) {
    return { byId, byName, sectionById, sectionTotals: plPeriod?.section_totals || {} };
  }
  for (const dl of plPeriod.detail_lines) {
    const v = Number(dl.value) || 0;
    if (dl.qbo_account_id) {
      const k = String(dl.qbo_account_id);
      byId.set(k, (byId.get(k) || 0) + v);
      // QBO-native section group (Income / COGS / Expenses /
      // OtherIncome / OtherExpenses). Set on first occurrence; an
      // account should appear in only one QBO P&L section group.
      if (dl.section && !sectionById.has(k)) sectionById.set(k, String(dl.section));
    }
    if (dl.name) {
      const nk = String(dl.name).toLowerCase().trim();
      byName.set(nk, (byName.get(nk) || 0) + v);
    }
  }
  return { byId, byName, sectionById, sectionTotals: plPeriod.section_totals || {} };
}

// ───────────────────────────────────────────────────────────────────────────
// V22.1 — Cross-section reclassification support.
//
// "Mapping is presentation" extends across sections, not just within.
// CPA staff routinely move accounts between IS sections — e.g.,
// reclassifying a consulting-fee Income account into Other Income for
// non-operating presentation, or moving an Expense account into Other
// Expense. QBO still classifies the account by its native AccountType
// (the QBO P&L section group), but the canonical mapping is the
// authoritative *presentation* — so the rendered IS must:
//   • show the account in the canonical destination section,
//   • adjust the QBO-sourced section totals so the rendered subtotal
//     matches the canonical leaf sum (no false "unmapped" residual),
//   • leave Net Income unchanged (so cf_ni_ties_to_is still holds).
//
// NI invariance holds only for SAME-SIGN reclassifications:
//   • income ↔ other_income (both credit-natural / NI: +)
//   • cogs ↔ opex ↔ other_expense (all debit-natural / NI: −)
// Cross-sign moves (e.g., income → other_expense) would flip NI by
// 2× the moved value vs QBO P&L NI. We refuse to net these and leave
// the residual firing so the data-quality signal surfaces.
// ───────────────────────────────────────────────────────────────────────────

// Canonical IS section keys ↔ QBO P&L section group identifiers
// (the `group` attribute on QBO ProfitAndLoss Section rows, captured
// server-side in _fetch_qbo_pl_period at server.py:2980).
const CANONICAL_TO_QBO_SECTION = {
  income:        'Income',
  cogs:          'COGS',
  opex:          'Expenses',
  other_income:  'OtherIncome',
  other_expense: 'OtherExpenses',
};
const QBO_TO_CANONICAL_SECTION = {
  Income:        'income',
  COGS:          'cogs',
  Expenses:      'opex',
  OtherIncome:   'other_income',
  OtherExpenses: 'other_expense',
};

// Sign group for cross-section NI-invariance.
//   credit-natural (NI: +): income, other_income
//   debit-natural  (NI: −): cogs, opex, other_expense
// Same-sign reclass: value flows AS-IS to destination (e.g. moving an
//   OpEx leaf into Other Expense; both reduce NI). Source -= v,
//   dest += v. NI preserved.
// Cross-sign reclass (V18, 2026-06-10): value flows as a CONTRA to
//   destination — the canonical destination shows the account at -v
//   (CPA-standard contra-expense / contra-income presentation). NI
//   preserved by construction: source -= v, dest += (-v) ⇒ both
//   sections shrink by v, net NI change = 0.
const SECTION_SIGN_GROUP = {
  income:        'credit',
  other_income:  'credit',
  cogs:          'debit',
  opex:          'debit',
  other_expense: 'debit',
};

// Build account_id → canonical IS section from the firm's mapping.
function buildAccountToCanonicalSection(mapping) {
  const out = new Map();
  for (const [sectionKey, items] of Object.entries(mapping || {})) {
    if (!CANONICAL_TO_QBO_SECTION[sectionKey]) continue;
    if (!Array.isArray(items)) continue;
    const walk = (it) => {
      if (!it) return;
      if (it.type === 'group') {
        for (const c of (it.children || [])) walk(c);
        return;
      }
      for (const aid of (it.accounts || [])) {
        out.set(String(aid), sectionKey);
      }
    };
    for (const it of items) walk(it);
  }
  return out;
}

// Compute per-section adjustment delta produced by cross-section
// reclassifications. Result is summed into QBO's raw sectionTotals.
//
// V18 (2026-06-10) — cross-sign reclassifications are now applied as
// CONTRA values into the destination section (preserving NI by
// construction). The previous V22.1 behavior of refusing cross-sign
// moves and emitting a residual line ITEM on the rendered face
// (the "QBO P&L variance" leaf) is removed; cross-sign moves are
// netted and the moved account renders under its canonical section
// at the contra (negated) value.
//
// Returns:
//   delta:              { income, cogs, opex, other_income, other_expense } —
//                       signed dollar amount to add to each canonical section
//                       total (negative for source section; positive for
//                       same-sign destination; negative for cross-sign
//                       destination since the moved value displays as
//                       contra under that section).
//   accounts:           list of {acctId, name, qboSection, canonicalSection,
//                       value, applied, crossSign} for diagnostics +
//                       optional UI badging.
//   effectiveValueByAcct: Map<acctId, dollar-value-as-rendered-in-canonical-
//                       section>. For same-section or same-sign reclass:
//                       the raw QBO value. For cross-sign reclass: -value
//                       (contra display). The leaf renderer consults this
//                       map so the account's printed dollar value carries
//                       the correct sign for its canonical section.
function computeReclassificationDelta(plPeriod, accountToCanonical) {
  const delta = { income: 0, cogs: 0, opex: 0, other_income: 0, other_expense: 0 };
  const accounts = [];
  const effectiveValueByAcct = new Map();
  for (const dl of (plPeriod?.detail_lines || [])) {
    if (!dl.qbo_account_id || !dl.section) continue;
    const aid = String(dl.qbo_account_id);
    const qboCanonical = QBO_TO_CANONICAL_SECTION[dl.section];
    const userCanonical = accountToCanonical.get(aid);
    if (!qboCanonical || !userCanonical) continue;
    if (qboCanonical === userCanonical) continue;
    const v = Number(dl.value) || 0;
    const sameSignGroup =
      SECTION_SIGN_GROUP[qboCanonical] === SECTION_SIGN_GROUP[userCanonical];
    accounts.push({
      acctId: aid,
      name: dl.name,
      qboSection: qboCanonical,
      canonicalSection: userCanonical,
      value: v,
      applied: true,
      crossSign: !sameSignGroup,
    });
    // Source section always loses `v`.
    delta[qboCanonical]  -= v;
    if (sameSignGroup) {
      // Destination gains `v` as a normal (positive) line.
      delta[userCanonical] += v;
      // Effective rendered value = +v (unchanged).
      effectiveValueByAcct.set(aid, (effectiveValueByAcct.get(aid) || 0) + v);
    } else {
      // Cross-sign: destination renders the account as a contra (-v).
      // delta on destination shrinks by v (adding -v as a contra).
      // NI invariance:
      //   ΔNI = (- contribution from source) + (+ contribution from dest as contra)
      //       = -(sign_source × v) + (sign_dest × (-v))
      // For credit→debit: sign_source=+1 (income contributes +), sign_dest=-1 (expense subtracts).
      //   ΔNI = -(+v) + ((-1) × (-v)) = -v + v = 0 ✓
      // For debit→credit: sign_source=-1 (expense subtracts), sign_dest=+1 (income adds).
      //   ΔNI = -(-v) + ((+1) × (-v)) = +v - v = 0 ✓
      delta[userCanonical] -= v;
      effectiveValueByAcct.set(aid, (effectiveValueByAcct.get(aid) || 0) - v);
    }
  }
  return { delta, accounts, effectiveValueByAcct };
}

// QBO returns IS values in PRESENTATION sign (revenue positive, expense
// positive). The canonical SECTION_SIGN for `income` is -1 (because TB
// returns income credit-natural and the IS builder flips). For V22 we
// receive PRESENTATION-positive values directly from QBO P&L, so we do
// NOT apply SECTION_SIGN. We DO use it for one thing only: deciding
// natural directionality of "section totals" so the NI math matches
// V20's convention (which the renderer expects).

function valueForAccountId(map, accountId, accountName) {
  if (!accountId && !accountName) return 0;
  if (accountId && map.byId.has(String(accountId))) return map.byId.get(String(accountId));
  if (accountName) {
    const lk = String(accountName).toLowerCase().trim();
    if (map.byName.has(lk)) return map.byName.get(lk);
  }
  return 0;
}

// Walk a canonical IS section's mapping items and produce a flat
// {label, value, prior, accounts:[], type:'leaf'|'group'} structure
// that the renderer consumes.
//
// V18 — `effectiveValueOverrides` (Map<acctId, +/-value>) carries the
// signed display value for accounts involved in CROSS-section
// reclassification. When present, the leaf uses the override (which
// may be a contra value for cross-sign moves) instead of the raw QBO
// value. Same-section accounts are absent from the map and fall back
// to the raw lookup.
function renderSection(items, accountsById, currentMap, priorMap,
                      effectiveCurrentOverrides, effectivePriorOverrides) {
  const lines = [];
  const walkLine = (item, parentGroupLabel = null) => {
    if (!item) return;
    if (item.type === 'group') {
      const groupLines = [];
      for (const c of (item.children || [])) {
        const childOut = walkLine(c, item.label);
        if (Array.isArray(childOut)) groupLines.push(...childOut);
      }
      // Emit the group as a non-leaf line that the renderer recognizes
      // (matches V20's shape).
      const groupCurrent = groupLines.reduce((s, l) => s + (l.current || 0), 0);
      const groupPrior   = groupLines.reduce((s, l) => s + (l.prior   || 0), 0);
      lines.push({
        label: item.label,
        type: 'group',
        current: groupCurrent,
        prior:   groupPrior,
        children: groupLines,
      });
      return [];  // children rolled up into group; no flat copy here
    }
    // Leaf: sum values across the leaf's accounts.
    let cur = 0, pri = 0;
    const accs = [];
    let leafHasCrossSign = false;
    for (const aid of (item.accounts || [])) {
      const a = accountsById[String(aid)];
      const accName = a?.name;
      const sid = String(aid);
      // V18 — cross-section reclass override. If the account was
      // reclassified into THIS section from a different QBO section,
      // the override map carries its effective (possibly contra) value.
      // Otherwise fall through to the raw QBO P&L lookup.
      if (effectiveCurrentOverrides && effectiveCurrentOverrides.has(sid)) {
        cur += effectiveCurrentOverrides.get(sid);
        leafHasCrossSign = leafHasCrossSign ||
          (effectiveCurrentOverrides.get(sid) < 0);  // contra signal
      } else {
        cur += valueForAccountId(currentMap, aid, accName);
      }
      if (effectivePriorOverrides && effectivePriorOverrides.has(sid)) {
        pri += effectivePriorOverrides.get(sid);
      } else {
        pri += valueForAccountId(priorMap, aid, accName);
      }
      accs.push(sid);
    }
    const leafOut = {
      label: item.label,
      type: 'leaf',
      current: cur,
      prior:   pri,
      accounts: accs,
    };
    if (leafHasCrossSign) leafOut.crossSign = true;  // optional UI badge hook
    lines.push(leafOut);
    return [leafOut];
  };
  for (const it of (items || [])) walkLine(it);
  return lines;
}

function sumLines(lines) {
  return (lines || []).reduce((s, l) => {
    if (l.type === 'group') return s + (l.current || 0);
    return s + (l.current || 0);
  }, 0);
}
function sumLinesPrior(lines) {
  return (lines || []).reduce((s, l) => {
    if (l.type === 'group') return s + (l.prior || 0);
    return s + (l.prior || 0);
  }, 0);
}

export function buildIncomeStatementV22(accounts, mapping, plPeriod, plPeriodPrior, opts = {}) {
  const accountsById = {};
  for (const a of (accounts || [])) accountsById[String(a.id)] = a;

  const currentMap = buildAccountValueMap(plPeriod, accounts);
  const priorMap   = buildAccountValueMap(plPeriodPrior, accounts);
  const hasPrior   = !!plPeriodPrior;

  // V22.1 — cross-section reclassification deltas. Computed once from
  // the canonical mapping × the current/prior period QBO detail_lines.
  // See computeReclassificationDelta() above for the principle.
  const accountToCanonical = buildAccountToCanonicalSection(mapping);
  const reclassCurrent = computeReclassificationDelta(plPeriod,      accountToCanonical);
  const reclassPrior   = computeReclassificationDelta(plPeriodPrior, accountToCanonical);

  const revenue      = renderSection(mapping?.income        || [], accountsById, currentMap, priorMap, reclassCurrent.effectiveValueByAcct, reclassPrior.effectiveValueByAcct);
  const cogs         = renderSection(mapping?.cogs          || [], accountsById, currentMap, priorMap, reclassCurrent.effectiveValueByAcct, reclassPrior.effectiveValueByAcct);
  const opex         = renderSection(mapping?.opex          || [], accountsById, currentMap, priorMap, reclassCurrent.effectiveValueByAcct, reclassPrior.effectiveValueByAcct);
  const otherIncome  = renderSection(mapping?.other_income  || [], accountsById, currentMap, priorMap, reclassCurrent.effectiveValueByAcct, reclassPrior.effectiveValueByAcct);
  const otherExpense = renderSection(mapping?.other_expense || [], accountsById, currentMap, priorMap, reclassCurrent.effectiveValueByAcct, reclassPrior.effectiveValueByAcct);

  // Section totals come from QBO P&L directly — these are the authoritative
  // period-scoped values. V22.1 adds the per-section reclassification
  // delta so the displayed section subtotal exactly matches the sum
  // of canonical leaves rendered above. Net Income is unchanged by
  // same-sign reclassifications (Σ delta == 0 across the IS), so the
  // cross-statement cf_ni_ties_to_is hard gate continues to hold.
  //
  // Mapping from canonical IS section → QBO P&L section group key
  // (set by _fetch_qbo_pl_period from QBO's row.group field):
  const qboTotal = (key) => Number(currentMap.sectionTotals?.[key]) || 0;
  const qboTotalPrior = (key) => Number(priorMap.sectionTotals?.[key]) || 0;
  const totalRevenue       = qboTotal('Income')        + reclassCurrent.delta.income;
  const totalCOGS          = qboTotal('COGS')          + reclassCurrent.delta.cogs;
  const totalOpex          = qboTotal('Expenses')      + reclassCurrent.delta.opex;
  const totalOtherIncome   = qboTotal('OtherIncome')   + reclassCurrent.delta.other_income;
  const totalOtherExpense  = qboTotal('OtherExpenses') + reclassCurrent.delta.other_expense;

  const grossProfit        = totalRevenue - totalCOGS;
  const incomeFromOps      = grossProfit - totalOpex;
  const netIncome          = incomeFromOps + totalOtherIncome - totalOtherExpense;

  const priorTotalRevenue       = qboTotalPrior('Income')        + reclassPrior.delta.income;
  const priorTotalCOGS          = qboTotalPrior('COGS')          + reclassPrior.delta.cogs;
  const priorTotalOpex          = qboTotalPrior('Expenses')      + reclassPrior.delta.opex;
  const priorTotalOtherIncome   = qboTotalPrior('OtherIncome')   + reclassPrior.delta.other_income;
  const priorTotalOtherExpense  = qboTotalPrior('OtherExpenses') + reclassPrior.delta.other_expense;
  const priorGrossProfit        = priorTotalRevenue - priorTotalCOGS;
  const priorIncomeFromOps      = priorGrossProfit - priorTotalOpex;
  const priorNetIncome          = priorIncomeFromOps + priorTotalOtherIncome - priorTotalOtherExpense;

  // V18 (2026-06-10) — residual line rendering REMOVED.
  //
  // The legacy "QBO P&L variance" leaves printed under each section were
  // a renderer plug: when canonical leaf sum ≠ adjusted QBO section
  // total, an inline "unmapped-residual" line silently absorbed the
  // delta. Under V22.1's same-sign-only reclass, this fired on every
  // cross-sign move and shipped diagnostic prose as account names on
  // client deliverables. With V18's contra-display reclass, all
  // SAME-account section moves balance to $0; the only remaining
  // residual cause is a TRULY-unmapped account (present in QBO P&L
  // detail_lines but in no canonical leaf). Those are now surfaced as
  // a hard validation gate ('is_no_section_residual' in
  // runValidationTies — fails closed, blocks PDF generation), not as
  // a printed face line.
  //
  // The per-section residual diagnostic below is what the validation
  // tie consumes. It mirrors the materiality threshold the gate uses
  // and lists the orphan account IDs so the staff editor can surface
  // them in the mapping UI.
  const materialityFor = (sectionTotal) =>
    Math.max(500, Math.abs(Number(sectionTotal) || 0) * 0.005);

  // Detect truly-unmapped accounts in the current period: account
  // appears in QBO P&L detail_lines but is in no canonical leaf at all
  // (after cross-section reclass — which would have placed it in some
  // canonical section). Sum per qbo-section so we can compare against
  // the adjusted total.
  const orphanByQboSection = { income: 0, cogs: 0, opex: 0, other_income: 0, other_expense: 0 };
  const orphanAccounts = [];
  for (const dl of (plPeriod?.detail_lines || [])) {
    const aid = dl.qbo_account_id ? String(dl.qbo_account_id) : null;
    if (!aid) continue;
    const qboCanonical = QBO_TO_CANONICAL_SECTION[dl.section];
    if (!qboCanonical) continue;
    if (accountToCanonical.has(aid)) continue;  // canonically mapped
    const v = Number(dl.value) || 0;
    if (Math.abs(v) < 0.005) continue;
    orphanByQboSection[qboCanonical] += v;
    orphanAccounts.push({
      acctId: aid,
      name: dl.name,
      qboSection: qboCanonical,
      value: v,
    });
  }

  const sectionResiduals = {
    revenue:       round2((totalRevenue       - sumLines(revenue))       - orphanByQboSection.income),
    cogs:          round2((totalCOGS          - sumLines(cogs))          - orphanByQboSection.cogs),
    opex:          round2((totalOpex          - sumLines(opex))          - orphanByQboSection.opex),
    otherIncome:   round2((totalOtherIncome   - sumLines(otherIncome))   - orphanByQboSection.other_income),
    otherExpense:  round2((totalOtherExpense  - sumLines(otherExpense))  - orphanByQboSection.other_expense),
  };
  const materialityBySection = {
    revenue:       materialityFor(totalRevenue),
    cogs:          materialityFor(totalCOGS),
    opex:          materialityFor(totalOpex),
    otherIncome:   materialityFor(totalOtherIncome),
    otherExpense:  materialityFor(totalOtherExpense),
  };

  // EBITDA — V20 derived this from incomeFromOps + D&A. V22 uses the
  // same convention; D&A comes from plPeriod's named-line regex (in
  // _fetch_qbo_pl_period). Falls back to 0 when plPeriod absent.
  const dAndA = (Number(plPeriod?.depreciation) || 0) + (Number(plPeriod?.amortization) || 0);
  const ebitda = incomeFromOps + dAndA;
  const priorDAndA = (Number(plPeriodPrior?.depreciation) || 0) + (Number(plPeriodPrior?.amortization) || 0);
  const priorEbitda = priorIncomeFromOps + priorDAndA;

  return {
    builder: 'v22',
    hasPrior,
    sections: {
      revenue, cogs, opex, otherIncome, otherExpense,
      // V20 also surfaces aliases `income`/`other_income`/etc.; keep
      // both names for renderer compatibility.
      income:        revenue,
      other_income:  otherIncome,
      other_expense: otherExpense,
    },
    totals: {
      totalRevenue, totalCOGS, grossProfit, totalOpex,
      incomeFromOps, totalOtherIncome, totalOtherExpense,
      netIncome, ebitda, dAndA,
    },
    prior: {
      totalRevenue:      priorTotalRevenue,
      totalCOGS:         priorTotalCOGS,
      grossProfit:       priorGrossProfit,
      totalOpex:         priorTotalOpex,
      incomeFromOps:     priorIncomeFromOps,
      totalOtherIncome:  priorTotalOtherIncome,
      totalOtherExpense: priorTotalOtherExpense,
      netIncome:         priorNetIncome,
      ebitda:            priorEbitda,
      dAndA:             priorDAndA,
    },
    diagnostic: {
      plPeriodNI:      Number(plPeriod?.net_income)      || 0,
      plPeriodPriorNI: Number(plPeriodPrior?.net_income) || 0,
      computedNI:      netIncome,
      computedPriorNI: priorNetIncome,
      niMatchesQbo:    Math.abs((Number(plPeriod?.net_income) || 0) - netIncome) < 0.5,
      detail_lines_current: (plPeriod?.detail_lines || []).length,
      detail_lines_prior:   (plPeriodPrior?.detail_lines || []).length,
      // V22.1 — cross-section reclassification audit. `accounts` lists
      // every account whose canonical IS section differs from QBO's
      // native section group. `applied:true` means the dollars were
      // netted into the destination section (NI invariance preserved);
      // `applied:false` means it was a sign-incompatible reclassification
      // and the residual advisory will still fire (data-quality signal).
      reclassification: {
        delta: reclassCurrent.delta,
        accounts: reclassCurrent.accounts,
        priorDelta: reclassPrior.delta,
      },
      // V18 — per-section residuals (after reclass deltas applied and
      // truly-unmapped orphan amounts deducted). In a healthy mapping,
      // every entry is ~$0. The `is_no_section_residual` validation tie
      // fails closed when any |residual| ≥ materialityBySection[k] OR
      // when any orphan accounts are detected, blocking PDF generation.
      sectionResiduals,
      materialityBySection,
      orphanAccounts,
      orphanByQboSection,
    },
  };
}

// Divergence diff for the V22 Day 2 side-by-side log.
// V20-IS vs V22-IS at the totals + leaf level.
export function diffIncomeStatements(v20, v22, ctx = {}) {
  const findings = [];
  const tol = 1.00;
  const push = (field, v20val, v22val) => {
    const a = Number(v20val) || 0;
    const b = Number(v22val) || 0;
    if (Math.abs(a - b) >= tol) {
      findings.push({ ...ctx, field, v20: a, v22: b, delta: b - a });
    }
  };
  push('totalRevenue',       v20?.totals?.totalRevenue,       v22?.totals?.totalRevenue);
  push('totalCOGS',          v20?.totals?.totalCOGS,          v22?.totals?.totalCOGS);
  push('grossProfit',        v20?.totals?.grossProfit,        v22?.totals?.grossProfit);
  push('totalOpex',          v20?.totals?.totalOpex,          v22?.totals?.totalOpex);
  push('incomeFromOps',      v20?.totals?.incomeFromOps,      v22?.totals?.incomeFromOps);
  push('totalOtherIncome',   v20?.totals?.totalOtherIncome,   v22?.totals?.totalOtherIncome);
  push('totalOtherExpense',  v20?.totals?.totalOtherExpense,  v22?.totals?.totalOtherExpense);
  push('netIncome',          v20?.totals?.netIncome,          v22?.totals?.netIncome);
  push('ebitda',             v20?.totals?.ebitda,             v22?.totals?.ebitda);
  push('PRIOR_totalRevenue', v20?.prior?.totalRevenue,        v22?.prior?.totalRevenue);
  push('PRIOR_netIncome',    v20?.prior?.netIncome,           v22?.prior?.netIncome);
  return findings;
}
